import React from 'react';
import { Gauge, Zap, LogOut, Sun, Moon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { User, Subscription, isProSubscriber } from '../../../shared/types';

export interface HeaderProps {
  user: User | null;
  subscription: Subscription | null;
  currentPath: string;
  theme?: 'light' | 'dark';
  onToggleTheme?: () => void;
  onNavigate: (path: string) => void;
  onOpenAuthModal: () => void;
  onLogout: () => void;
}

export function Header({
  user,
  subscription,
  currentPath,
  theme = 'light',
  onToggleTheme,
  onNavigate,
  onOpenAuthModal,
  onLogout,
}: HeaderProps) {
  const isPro = isProSubscriber(subscription);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
      e.preventDefault();
      onNavigate(path);
    }
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/80 bg-background/95 dark:bg-slate-950/90 backdrop-blur-md">
      {/* Mobile Apps Coming Soon Banner */}
      <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/15 to-amber-500/10 border-b border-amber-500/20 py-1.5 px-4 text-center text-xs font-medium text-amber-700 dark:text-amber-300 flex items-center justify-center gap-2">
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-slate-950 uppercase tracking-wider">
          Coming Soon
        </span>
        <span className="hidden sm:inline">📱 Native iOS &amp; Android apps launching soon for offline study &amp; synchronized progress!</span>
        <span className="sm:hidden">📱 Native iOS &amp; Android apps launching soon!</span>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full flex h-16 items-center justify-between gap-3">
        {/* Brand / Logo (Single line) */}
        <a
          href={user ? '/dashboard' : '/'}
          onClick={(e) => handleLinkClick(e, user ? '/dashboard' : '/')}
          className="flex items-center gap-2.5 cursor-pointer select-none group shrink-0"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 shadow-md shadow-amber-500/20 group-hover:scale-105 transition-transform">
            <Gauge className="h-5 w-5 text-slate-950 stroke-[2.5]" aria-hidden="true" />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-base sm:text-lg tracking-tight text-foreground group-hover:text-amber-500 transition-colors whitespace-nowrap">
              Maintenance<span className="text-amber-500">Prep</span>
            </span>
            <Badge variant="warning" className="text-[10px] px-1.5 py-0 font-bold whitespace-nowrap">PRO</Badge>
          </div>
        </a>

        {/* Primary Navigation (Semantic <a> links for crawlers & SPA navigation) */}
        <nav aria-label="Primary Navigation" className="hidden md:flex items-center gap-1 xl:gap-2 text-xs lg:text-sm font-medium">
          {user ? (
            <a
              href="/dashboard"
              onClick={(e) => handleLinkClick(e, '/dashboard')}
              aria-current={currentPath === '/dashboard' || currentPath === '/' ? 'page' : undefined}
              className={`px-2.5 lg:px-3 py-1.5 rounded-md transition-colors whitespace-nowrap ${
                currentPath === '/dashboard' || currentPath === '/'
                  ? 'text-amber-600 dark:text-amber-400 bg-amber-500/10 font-bold'
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60'
              }`}
            >
              Dashboard
            </a>
          ) : (
            <a
              href="/"
              onClick={(e) => handleLinkClick(e, '/')}
              aria-current={currentPath === '/' ? 'page' : undefined}
              className={`px-2.5 lg:px-3 py-1.5 rounded-md transition-colors whitespace-nowrap ${
                currentPath === '/'
                  ? 'text-amber-600 dark:text-amber-400 bg-amber-500/10 font-bold'
                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60'
              }`}
            >
              Overview
            </a>
          )}
          <a
            href="/track/mechanical"
            onClick={(e) => handleLinkClick(e, '/track/mechanical')}
            aria-current={currentPath.startsWith('/track/mechanical') ? 'page' : undefined}
            className={`px-2.5 lg:px-3 py-1.5 rounded-md transition-colors whitespace-nowrap ${
              currentPath.startsWith('/track/mechanical') ? 'text-amber-600 dark:text-amber-400 bg-amber-500/10 font-bold' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60'
            }`}
          >
            Mechanical
          </a>
          <a
            href="/track/electrical"
            onClick={(e) => handleLinkClick(e, '/track/electrical')}
            aria-current={currentPath.startsWith('/track/electrical') ? 'page' : undefined}
            className={`px-2.5 lg:px-3 py-1.5 rounded-md transition-colors whitespace-nowrap ${
              currentPath.startsWith('/track/electrical') ? 'text-sky-600 dark:text-sky-400 bg-sky-500/10 font-bold' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60'
            }`}
          >
            Electrical
          </a>
          <a
            href="/track/multicraft"
            onClick={(e) => handleLinkClick(e, '/track/multicraft')}
            aria-current={currentPath.startsWith('/track/multicraft') ? 'page' : undefined}
            className={`px-2.5 lg:px-3 py-1.5 rounded-md transition-colors whitespace-nowrap ${
              currentPath.startsWith('/track/multicraft') ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 font-bold' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60'
            }`}
          >
            Multicraft &amp; 955
          </a>
          <a
            href="/track/aptitude"
            onClick={(e) => handleLinkClick(e, '/track/aptitude')}
            aria-current={currentPath.startsWith('/track/aptitude') ? 'page' : undefined}
            className={`px-2.5 lg:px-3 py-1.5 rounded-md transition-colors whitespace-nowrap ${
              currentPath.startsWith('/track/aptitude') ? 'text-purple-600 dark:text-purple-400 bg-purple-500/10 font-bold' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60'
            }`}
          >
            Aptitude
          </a>
          <a
            href="/course/schematics"
            onClick={(e) => handleLinkClick(e, '/course/schematics')}
            aria-current={currentPath === '/course/schematics' || currentPath === '/schematics' ? 'page' : undefined}
            className={`px-2.5 lg:px-3 py-1.5 rounded-md transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              currentPath === '/course/schematics' || currentPath === '/schematics'
                ? 'text-sky-600 dark:text-sky-400 bg-sky-500/10 font-bold'
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60'
            }`}
          >
            <span>Schematics Course</span>
            <span className="text-[10px] font-bold px-1 py-0.2 rounded bg-amber-500/20 text-amber-500 dark:text-amber-400">NEW</span>
          </a>
          <a
            href="/study-lab"
            onClick={(e) => handleLinkClick(e, '/study-lab')}
            aria-current={currentPath === '/study-lab' || currentPath === '/flashcards' || currentPath === '/formulas' ? 'page' : undefined}
            className={`px-2.5 lg:px-3 py-1.5 rounded-md transition-colors whitespace-nowrap ${
              currentPath === '/study-lab' || currentPath === '/flashcards' || currentPath === '/formulas'
                ? 'text-amber-600 dark:text-amber-400 bg-amber-500/10 font-bold'
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60'
            }`}
          >
            Study Lab
          </a>
          <a
            href="/pricing"
            onClick={(e) => handleLinkClick(e, '/pricing')}
            aria-current={currentPath === '/pricing' ? 'page' : undefined}
            className={`px-2.5 lg:px-3 py-1.5 rounded-md transition-colors whitespace-nowrap ${
              currentPath === '/pricing' ? 'text-amber-600 dark:text-amber-400 bg-amber-500/10 font-bold' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60'
            }`}
          >
            Pricing
          </a>
        </nav>

        {/* User Account / Auth Actions */}
        <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
          {/* Trial CTA link for non-pro users */}
          {!isPro && (
            <Button
              variant="amber"
              size="sm"
              onClick={() => onNavigate('/pricing')}
              className="hidden xl:inline-flex text-xs font-bold gap-1 shadow-sm shadow-amber-500/20 whitespace-nowrap"
            >
              <Zap className="h-3 w-3 fill-current" aria-hidden="true" />
              14-Day Free Trial
            </Button>
          )}

          {/* Theme Toggle Button */}
          {onToggleTheme && (
            <button
              type="button"
              onClick={onToggleTheme}
              className="p-2 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 text-amber-400 transition-transform hover:rotate-45" aria-hidden="true" />
              ) : (
                <Moon className="h-4 w-4 text-slate-700 transition-transform hover:-rotate-12" aria-hidden="true" />
              )}
            </button>
          )}

          {user ? (
            <div className="flex items-center gap-2 shrink-0">
              <a
                href="/dashboard"
                onClick={(e) => handleLinkClick(e, '/dashboard')}
                className="flex items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-foreground hover:border-slate-400 dark:hover:border-slate-600 transition-all shadow-sm whitespace-nowrap"
              >
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name}
                    width={24}
                    height={24}
                    className="h-6 w-6 rounded-full object-cover border border-amber-500/40"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="hidden sm:flex items-center gap-1.5 whitespace-nowrap">
                  <span className="font-semibold text-foreground text-xs">{user.name}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-slate-100 dark:bg-slate-800 text-muted-foreground uppercase border border-border/80">
                    {subscription?.status === 'trialing' ? 'Trial' : isPro ? 'PRO' : 'Free'}
                  </span>
                </div>
              </a>
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                className="text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 p-2 shrink-0"
                aria-label="Log Out"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="primary"
                size="sm"
                onClick={onOpenAuthModal}
                className="whitespace-nowrap"
              >
                Sign In
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
